import { takeLatest, put, call, select, takeEvery } from 'redux-saga/effects';
import restClient from 'erpcore/api/restClient';
import dto from 'erpcore/utils/dto';
import moment from 'moment-timezone';
import { getMeData } from 'erpcore/utils/AuthManager/AuthManager.selectors';

import { getQueryParams } from 'erpcore/components/Listing/Listing.selectors';

import { actions as listingActions } from 'erpcore/components/Listing/Listing.reducer';
import { actions as notificationManagerActions } from 'erpcore/utils/NotificationManager/NotificationManager.reducer';
import { actions as authActions } from 'erpcore/utils/AuthManager/AuthManager.reducer';
import { actions as usersActions } from './Users.reducer';

const usersListingIncludeString =
    'include=image,image.versions,office,jobTitle,department,reportsTo,company';

/**
 * Create User
 * @param  {Object} promise
 * @return {Object} formData
 */

export function* createUser({ promise, formData }) {
    try {
        const createUserAPI = yield restClient.post(`/api/users`, formData);
        yield put({
            type: usersActions.CREATE_USER_SUCCESSFUL
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: createUserAPI?.data
        });
        yield call(promise.resolve, createUserAPI?.data);
    } catch (error) {
        yield put({
            type: usersActions.CREATE_USER_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Fetch User
 * @param  {Object} promise
 * @return {string} id User id
 */
export function* fetchUser({ promise, iri, params }) {
    try {
        const fetchUserAPI = yield restClient.get(iri, { params });
        yield put({
            type: usersActions.FETCH_SINGLE_USER_SUCCESSFUL
        });
        yield put({
            type: usersActions.STORE_SINGLE_USER_DATA,
            iri,
            response: dto(fetchUserAPI?.data)
        });
        yield call(promise.resolve, fetchUserAPI?.data);
    } catch (error) {
        yield put({
            type: usersActions.FETCH_SINGLE_USER_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Update User single data
 * @param  {Object} id ID of an User
 * @return {Object} Response from API
 */
export function* updateSingleUser({ promise, formData, iri }) {
    try {
        const updateSingleUserAPI = yield restClient.put(
            `${iri}?include=organizations,country,state,city,image,roles,permissions,timezone,department,jobTitle,office,division,reportsTo,company,activeCapacitySchedule&sync_method=INSTANT`,
            formData
        );
        const dtoUpdateSingleUserAPI = yield dto(updateSingleUserAPI?.data);
        yield put({
            type: usersActions.UPDATE_SINGLE_USER_SUCCESSFUL
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: updateSingleUserAPI?.data
        });
        yield put({
            type: usersActions.STORE_SINGLE_USER_DATA,
            iri,
            response: dtoUpdateSingleUserAPI
        });
        // Check if I updated myself
        // if true, Fetch Me Data
        const currentUser = yield select(getMeData);
        const isUpdatingMyself = yield currentUser?.iri === iri;
        if (isUpdatingMyself) {
            // update "me" if update-id matches me-id
            yield put({
                promise,
                type: authActions.START_FETCHING_ME
            });
        }
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.UPDATE_SINGLE_USER_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Delete Single User
 * @param  {Object} id ID of an User
 * @return {Object} Response from API
 */
export function* deleteSingleUser({ promise, iri }) {
    try {
        const deleteSingleUserAPI = yield restClient.delete(iri);
        yield put({
            type: usersActions.DELETE_SINGLE_USER_SUCCESSFUL
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: deleteSingleUserAPI?.data
        });
        const params = yield select(getQueryParams, { name: 'users' });
        yield put({
            type: listingActions.START_FETCHING_LISTING,
            params,
            entity: 'USERS',
            name: 'users',
            endpoint: `api/users?${usersListingIncludeString}`
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.DELETE_SINGLE_USER_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Change Password Single User
 * @param  {Object} id ID of an User
 * @return {Object} Response from API
 */
export function* changePasswordSingleUser({ promise, formData, id }) {
    try {
        const changePasswordSingleUserAPI = yield restClient.patch(
            `/api/users/${id}/password`,
            formData
        );
        yield put({
            type: usersActions.CHANGE_PASSWORD_SINGLE_USER_SUCCESSFUL
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: changePasswordSingleUserAPI?.data
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.CHANGE_PASSWORD_SINGLE_USER_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Fetch User Estimated Hours To Log
 * @param  {Object} promise
 * @return {string} iri User iri
 */
export function* fetchUserEstimatedHoursToLog({ promise, iri }) {
    try {
        const fetchUserEstimatedHoursToLogAPI = yield restClient.get(
            `${iri}/estimated-hours?filters[archived][equals]=all`
        );
        yield put({
            type: usersActions.FETCH_USER_ESTIMATED_HOURS_TO_LOG_SUCCESSFUL
        });
        yield put({
            type: usersActions.STORE_USER_ESTIMATED_HOURS_TO_LOG,
            iri,
            response: fetchUserEstimatedHoursToLogAPI
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.FETCH_USER_ESTIMATED_HOURS_TO_LOG_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Update User working days
 */
export function* updateUserWorkingDays({ promise, formData, iri }) {
    try {
        if (formData?.working_days?.method === 'patch') {
            yield restClient.patch(formData?.working_days?.iri, formData?.working_days);
        } else {
            yield restClient.post('/api/user-capacity-schedules', {
                ...formData?.working_days,
                user: iri
            });
        }
        yield put({
            type: usersActions.UPDATE_USER_WORKING_DAYS_SUCCESSFUL
        });
        // Check if I updated myself
        // if true, Fetch Me Data
        const currentUser = yield select(getMeData);
        const isUpdatingMyself = yield currentUser?.iri === iri;
        if (isUpdatingMyself) {
            // update "me" if update-id matches me-id
            yield put({
                promise,
                type: authActions.START_FETCHING_ME
            });
        }
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.UPDATE_USER_WORKING_DAYS_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

export function* fetchUserCumulativeHoursWorked({ promise, iri }) {
    try {
        const startOfMonthDate = moment()
            .startOf('month')
            .format('YYYY-MM-DD');

        const todayDate = moment().format('YYYY-MM-DD');

        const apiResponse = yield restClient.get(`/api/reports/time-logs`, {
            params: {
                display_by: 'day',
                'filters[date][between]': [startOfMonthDate, todayDate],
                'filters[created_by][equals]': iri,
                type: 'cumulative',
                'filters[status][not_in]': ['draft', 'paused'],
                'filters[archived][equals]': 'all'
            },
            headers: { Accept: 'application/json' }
        });

        yield put({
            type: usersActions.FETCH_USER_CUMULATIVE_HOURS_WORKED_SUCCESSFUL
        });

        yield put({
            type: usersActions.STORE_USER_CUMULATIVE_HOURS_WORKED,
            iri,
            response: apiResponse.data.data
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: usersActions.FETCH_USER_CUMULATIVE_HOURS_WORKED_FAILED
        });
        yield put({
            type: notificationManagerActions.ADD_FLOATING_NOTIFICATION,
            response: error?.response?.data || error
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}
/**
 * Register action to watcher
 */
export const usersSaga = [
    takeLatest(usersActions.START_CREATE_USER, createUser),
    takeLatest(usersActions.START_FETCHING_SINGLE_USER, fetchUser),
    takeEvery(usersActions.START_UPDATE_SINGLE_USER, updateSingleUser),
    takeLatest(usersActions.START_DELETE_SINGLE_USER, deleteSingleUser),
    takeLatest(usersActions.START_CHANGE_PASSWORD_SINGLE_USER, changePasswordSingleUser),
    takeLatest(
        usersActions.START_FETCH_USER_CUMULATIVE_HOURS_WORKED,
        fetchUserCumulativeHoursWorked
    ),
    takeLatest(usersActions.START_FETCH_USER_ESTIMATED_HOURS_TO_LOG, fetchUserEstimatedHoursToLog),
    takeLatest(usersActions.START_UPDATE_USER_WORKING_DAYS, updateUserWorkingDays)
];
